/**
 * ReadMore Web Component
 * A custom HTML element that provides read more/less functionality
 */
class ReadMore extends HTMLElement {
  constructor() {
    super();
    this.isExpanded = false;
    this.readMoreText = 'read more';
    this.readLessText = 'read less';
    this.threshold = 50; // Minimum character difference to show read more
  }

  static get observedAttributes() {
    return ['read-more-text', 'read-less-text', 'threshold'];
  }

  connectedCallback() {
    this.initializeComponent();
    this.setupEventListeners();
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      switch (name) {
        case 'read-more-text':
          this.readMoreText = newValue;
          break;
        case 'read-less-text':
          this.readLessText = newValue;
          break;
        case 'threshold':
          this.threshold = parseInt(newValue) || 50;
          break;
      }
      this.updateButtonText();
    }
  }

  /**
   * Initialize the component by setting up the initial state
   */
  initializeComponent() {
    this.readMoreLinks = this.querySelectorAll('.js-read-more-link');
    this.contentFull = this.querySelector('.js-content-full');
    this.contentShort = this.querySelector('.js-content-short');

    if (!this.readMoreLinks || !this.contentFull || !this.contentShort) {
      console.warn('ReadMore component: Required elements not found');
      return;
    }

    this.setInitialState();
  }

  /**
   * Set the initial state based on content length
   */
  setInitialState() {
    const fullText = this.contentFull.textContent.trim();
    const shortText = this.contentShort.textContent.trim();

    if (fullText.length <= shortText.length + this.threshold) {
      // Content is short, hide read more functionality
      this.readMoreLink.style.display = 'none';
      this.contentFull.style.display = 'block';
      this.contentShort.style.display = 'none';
      this.isExpanded = true;
    } else {
      // Content is long enough, show read more functionality
      // Don't change the button text initially - keep original "Read more"
      this.collapse(false);
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    if (this.readMoreLinks) {
      this.boundToggleHandler = this.toggle.bind(this);
      this.readMoreLinks.forEach(link => link.addEventListener('click', this.boundToggleHandler));
    }
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    if (this.readMoreLinks && this.boundToggleHandler) {
      this.readMoreLinks.forEach(link => link.removeEventListener('click', this.boundToggleHandler));
    }
  }

  /**
   * Toggle between expanded and collapsed states
   */
  toggle(event) {
    event.preventDefault();
    
    if (this.isExpanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  /**
   * Expand the content to show full text
   */
  expand() {
    this.isExpanded = true;
    this.classList.add('expanded');
    this.updateButtonText();
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('readmore:expand', {
      detail: { element: this },
      bubbles: true
    }));
  }

  /**
   * Collapse the content to show short text
   */
  collapse(updateText = true) {
    this.isExpanded = false;
    this.classList.remove('expanded');
    
    // Only update button text if explicitly requested (not on initial load)
    if (updateText) {
      this.updateButtonText();
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('readmore:collapse', {
      detail: { element: this },
      bubbles: true
    }));
  }

  /**
   * Update the button text based on current state
   */
  updateButtonText() {
    if (this.readMoreLinks) {
      this.readMoreLinks.forEach(link => link.textContent = this.isExpanded ? this.readLessText : this.readMoreText);
    }
  }

  /**
   * Public method to programmatically expand
   */
  expandContent() {
    this.expand();
  }

  /**
   * Public method to programmatically collapse
   */
  collapseContent() {
    this.collapse();
  }

  /**
   * Public method to check if content is expanded
   */
  isContentExpanded() {
    return this.isExpanded;
  }
}

// Register the custom element only if it doesn't already exist
if (!customElements.get('read-more')) {
  customElements.define('read-more', ReadMore);
}


